import { View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  DATE_FORMAT_PREFS,
  dateOrderOf,
  effectiveDateLocale,
  formatShortDate,
  type DateFormatPref,
} from "@heraion/shared";
import type { SupportedLng } from "@heraion/i18n";

import { useDateFormatPref } from "../primitives/DateFormatContext";
import type { UiTheme } from "../theme";
import { PreauthLanguagePicker } from "./PreauthLanguagePicker";
import { SettingsPillChoice } from "./SettingsPillChoice";

// #390: "Language & region", the one Settings entry for the app language and
// the date format. Mobile and the web console both mount this panel; each host
// owns how the language is applied and persisted (`onPickLanguage`), the date
// format lives in the DateFormatProvider both hosts mount.

// 22 November: the day cannot be mistaken for a month, so every option's
// example reads unambiguously.
// No "Z": read in the device zone, so the day stays the 22nd in every zone.
const EXAMPLE_ISO = "2026-11-22T12:00:00";

export function LanguageRegionPanel({
  theme,
  language,
  onPickLanguage,
}: {
  theme: UiTheme;
  language: SupportedLng;
  onPickLanguage: (lng: SupportedLng) => void;
}) {
  const { t } = useTranslation();
  const { pref, setPref, deviceLocale } = useDateFormatPref();

  const name = (p: DateFormatPref): string =>
    t(`settings.dateFormat.${p}`, {
      defaultValue: { auto: "Automatic", dmy: "Day first", mdy: "Month first", ymd: "Year first" }[p],
    });

  // "Automatic" says what it resolves to on this device.
  const autoOrder = dateOrderOf(effectiveDateLocale(language, "auto", deviceLocale));
  const optionName = (p: DateFormatPref): string =>
    p === "auto" && autoOrder
      ? t("settings.dateFormat.autoResolved", {
          order: name(autoOrder),
          defaultValue: "Automatic ({{order}})",
        })
      : name(p);

  return (
    <View style={{ gap: theme.spacing.s3 }}>
      <PreauthLanguagePicker theme={theme} value={language} onPick={onPickLanguage} />
      <SettingsPillChoice<DateFormatPref>
        theme={theme}
        labelIcon="calendar"
        label={t("settings.dateFormat.label", { defaultValue: "Date format" })}
        help={t("settings.dateFormat.help", {
          defaultValue:
            "Where the day and the month go. Automatic follows the region of this device.",
        })}
        options={DATE_FORMAT_PREFS.map((p) => ({
          value: p,
          // The example is the real formatter's output, so an option that
          // changes nothing in this language shows it.
          label: optionName(p),
          example: formatShortDate(
            EXAMPLE_ISO,
            effectiveDateLocale(language, p, deviceLocale),
          ),
        }))}
        value={pref}
        onPick={setPref}
      />
    </View>
  );
}
